import React, {useEffect, useState} from 'react';
import Dashboard from './SideBar';
import { useAuth } from "../contexts/authContext";
import { db } from "../firebase/firebase";
import {useDarkMode} from "../contexts/darkModeContext";
import {
    BeakerIcon,
    CalculatorIcon,
    CloudIcon,
    MapPinIcon,
    SunIcon,
    TruckIcon,
    UserGroupIcon
} from "@heroicons/react/24/outline";
import {Link} from "react-router-dom";
import course from "../photos/course.jpg";

import { ReactComponent as RTL } from '../Directions/Right-To-Left.svg';
import { ReactComponent as LTR } from '../Directions/Left-To-Right.svg';
import { ReactComponent as FTB } from '../Directions/Front-To-Back.svg';
import { ReactComponent as CRO } from '../Directions/Cross.svg';
import { ReactComponent as RTLS } from '../Directions/Right-To-Left-Shade.svg';
import { ReactComponent as LTRS } from '../Directions/Left-To-Right-Shade.svg';
import { ReactComponent as FTBS } from '../Directions/Front-To-Back-Shade.svg';
import { ReactComponent as CLEAN } from '../Directions/CleanUp.svg';
import { ReactComponent as NOCLEAN } from '../Directions/NoCleanUp.svg';
import { ReactComponent as STS } from '../Directions/Side-To-Side.svg';
import { ReactComponent as BEE } from '../Directions/BeeHive.svg';

const Home = () => {
    const { currentUser } = useAuth();

    const { darkMode } = useDarkMode();

    const [chems, setChems] = useState([]);
    const [sprays, setSprays] = useState([]);
    const [fleet, setFleet] = useState([]);
    const [employees, setEmployees] = useState([]);

    const [address, setAddress] = useState('');
    const [city, setCity] = useState("");
    const [state, setState] = useState("");

    const [courses, setCourses] = useState([]);


    useEffect(() => {

        const fetchChems = async () => {
            if (currentUser) {
                try {
                    const storedChemsData = localStorage.getItem(`chemsData_${currentUser.uid}`);
                    if (storedChemsData) {
                        const chemsData = JSON.parse(storedChemsData);
                        setChems(chemsData);
                    } else {
                        setChems([]);
                    }
                } catch (error) {
                    console.error('Error fetching chems:', error);
                }
            }
        };

        const fetchEmployees = async () => {
            if (currentUser) {
                try {
                    const storedTeamData = localStorage.getItem(`teamData_${currentUser.uid}`);
                    if (storedTeamData) {
                        const teamData = JSON.parse(storedTeamData);
                        setEmployees(teamData);
                    } else {
                        setEmployees([]);
                    }
                } catch (error) {
                    console.error('Error fetching employees:', error);
                }
            }
        };

        const fetchSprays = async () => {
            if (currentUser) {
                try {
                    const storedSpraysData = localStorage.getItem(`spraysData_${currentUser.uid}`);
                    if (storedSpraysData) {
                        const spraysData = JSON.parse(storedSpraysData);
                        setSprays(spraysData);
                    } else {
                        setSprays([]);
                    }
                } catch (error) {
                    console.error('Error fetching sprays:', error);
                }
            }
        };

        const fetchFleet = async () => {
            if (currentUser) {
                try {
                    const storedFleetData = localStorage.getItem(`fleetData_${currentUser.uid}`);
                    if (storedFleetData) {
                        const fleetData = JSON.parse(storedFleetData);
                        setFleet(fleetData);
                    } else {
                        setFleet([]);
                    }
                } catch (error) {
                    console.error('Error fetching fleet:', error);
                }
            }
        };

        const fetchUserData = async () => {
            if (currentUser) {
                try {
                    // Fetch user document from Firestore based on the current user's UID
                    const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                    if (userDoc.exists) {
                        // Get the user data from the document
                        const userData = userDoc.data();

                        setAddress(userData.address || ""); // Default to false if metric field doesn't exist
                        setCity(userData.city || "");
                        setState(userData.state || "");

                    } else {
                        console.log('User document not found');
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };

        const fetchCourses = async () => {
            if (currentUser) {
                try {
                    const storedCourseData = localStorage.getItem(`courseData_${currentUser.uid}`);
                    if (storedCourseData) {
                        const courseData = JSON.parse(storedCourseData);
                        setCourses(courseData);
                    } else {
                        const coursesCollectionRef = db.collection('Users').doc(currentUser.uid).collection('courses');
                        const snapshot = await coursesCollectionRef.get();

                        if (!snapshot.empty) {
                            const courseData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setCourses(courseData);
                            localStorage.setItem(`courseData_${currentUser.uid}`, JSON.stringify(courseData));
                        } else {
                            console.log('No courses found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching courses:', error);
                }
            }
        };

        fetchUserData();

        fetchSprays();
        fetchChems();
        fetchEmployees();
        fetchFleet();
        fetchCourses();
    }, [currentUser, address]);



    const projects = [
        { name: 'Product Inventory', href: '/content?nav=Product%20Inventory', members: chems.length, icon: BeakerIcon, label: 'Products' },
        { name: 'Spray History', href: '/content?nav=Spray%20Calculator', members: sprays.length, icon: CalculatorIcon, label: 'Sprays' },
        { name: 'Fleet Size', href: '/content?nav=Fleet%20Management', members: fleet.length, icon: TruckIcon, label: 'Machines' },
        { name: 'Team Size', href: '/content?nav=Team%20Management', members: employees.length, icon: UserGroupIcon, label: 'Employees' },
    ]

    const CourseStats = [
        { name: 'Greens', value: `${courses.reduce((sum, course) => sum + (parseFloat(course.greensTotal) || 0), 0)}`, unit: 'ac' },
        { name: 'Fairways', value: `${courses.reduce((sum, course) => sum + (parseFloat(course.fairwaysTotal) || 0), 0)}`, unit: 'ac' },
        { name: 'Tees', value: `${courses.reduce((sum, course) => sum + (parseFloat(course.teesTotal) || 0), 0)}`, unit: 'ac' },
        { name: 'Rough', value: `${courses.reduce((sum, course) => sum + (parseFloat(course.roughTotal) || 0), 0)}`, unit: 'ac' },
    ]

    const date = new Date();

    // Get the day of the week
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

    // Helper function to format with suffix
    const formatDateWithSuffix = (date) => {
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();

        const getSuffix = (d) => {
            if (d > 3 && d < 21) return 'th';
            switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        return `${month} ${day}${getSuffix(day)}, ${year}`;
    };

    const formattedDate = formatDateWithSuffix(date);


    return (
        <div className={`${darkMode ? " text-white" : " text-black"} p-3 flex h-full w-full`}>
            {/*<Dashboard/>*/}

            <div className="z-20 content w-full flex flex-col animate-fadeIn">


                {/*Totals*/}
                <div className={`mb-3`}>
                    <ul role="list" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {projects.map((project) => (
                            <li key={project.name} className="col-span-1 flex rounded-md shadow-sm">
                                <div
                                    className={'flex bg-green-600 w-16 shrink-0 items-center justify-center rounded-l-md text-sm font-medium text-white'}
                                >
                                    <project.icon aria-hidden="true" className="size-6 text-white hover:animate-heartbeat"/>
                                </div>
                                <div
                                    className={`flex flex-1 items-center justify-between truncate rounded-r-md border-b border-r border-t ${darkMode ? 'bg-bgDark border-neutral-700 text-white' : 'bg-white text-black'}`}>
                                    <div className="flex-1 truncate px-4 py-2 text-sm">
                                        <Link to={project.href}
                                           className="font-medium hover:tracking-widest">
                                            {project.name}
                                        </Link>
                                        <p className="text-gray-500">{project.members} {project.label}</p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>


                {/*Home Page Blocks*/}
                <div className={`grid h-full max-h-[calc(100vh-9.75rem)] grid-cols-6 grid-rows-4 gap-3`}>

                    {/*Main Data Points*/}
                    <div className={`col-span-6 col-start-1 border ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white'} rounded-lg`}>

                        {/*<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">*/}
                        {/*    <path d="M4 20L20 4" stroke="white" strokeLinecap="round"/>*/}
                        {/*    <path d="M8 22.0069L22.0069 8" stroke="white" strokeLinecap="round"/>*/}
                        {/*    <path d="M2 16L16 2" stroke="white" strokeLinecap="round"/>*/}
                        {/*    <path d="M13.7434 22.7566L22.7449 13.7551" stroke="white" strokeLinecap="round"/>*/}
                        {/*    <path d="M1 11L10 2" stroke="white" strokeLinecap="round"/>*/}
                        {/*</svg>*/}

                        <div className={`flex gap-3`}>
                            <RTL className={`size-16`}/>
                            <LTR className={`size-16`}/>
                            <FTB className={`size-16`}/>
                            <CRO className={`size-16`}/>
                            <RTLS className={`size-16`}/>
                            <LTRS className={`size-16`}/>
                            <FTBS className={`size-16`}/>
                            <CLEAN className={`size-16`}/>
                            <NOCLEAN className={`size-16`}/>
                            <STS className={`size-16`}/>
                            <BEE className={`size-16`}/>
                        </div>


                    </div>

                    {/*Weather*/}
                    <div className={`col-span-2 row-span-3 col-start-1 row-start-2 relative overflow-hidden border text-white ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-bgDark'} rounded-lg`}>
                        <img
                            src={course}
                            alt="background"
                            className="absolute rounded-lg opacity-60 inset-0 w-full h-full object-cover"
                        />

                        <div className="relative z-10 grid grid-cols-4 grid-rows-3 gap-4 h-full p-2">

                            {/*Day Of Week*/}
                            <div className="col-span-4 w-full grid grid-cols-3 col-start-1">
                                <div className={`col-span-2 flex flex-col space-y-2`}>
                                    <span className={`text-6xl tracking-widest`}>{dayOfWeek}</span>
                                    <span className={`text-xl tracking-widest`}>{formattedDate}</span>
                                    <div className={`flex items-center text-green-400`}>
                                        <MapPinIcon className={`size-6`}/>
                                        <div className={``}>{city}, {state}</div>
                                    </div>
                                </div>

                                <div className={`col-span-1 flex justify-center items-center`}>
                                    <div className="flex items-center justify-center text-4xl">50 <span className="ml-1 text-lg">°F</span></div>
                                    <SunIcon className="size-24 text-yellow-400"/>
                                    {/*<CloudIcon className="size-24 text-gray-300"/>*/}
                                </div>
                            </div>

                            {/*Temp*/}
                            <div className="col-span-full row-span-2 col-start-1 row-start-2">
                                <div className="flex flex-col justify-between h-full">
                                    <div className="flex items-center justify-end text-6xl">50 <span className="ml-1 text-xl">°F</span></div>
                                    <div className="flex items-center justify-start text-2xl">Partly Sunny</div>
                                </div>
                            </div>

                        </div>

                        {/*<div className="relative z-10 grid grid-cols-3">*/}

                        {/*<div className={`col-span-full flex flex-col`}>*/}
                            {/*    <span className={`text-6xl tracking-widest`}>{dayOfWeek}</span>*/}
                            {/*    <span className={`text-xl`}>{formattedDate}</span>*/}
                            {/*    <div className={``}></div>*/}
                            {/*</div>*/}

                        {/*    <div className="col-span-2 flex flex-col justify-center">*/}
                        {/*        <div className="ml-4 tracking-tighter flex items-center text-4xl">*/}
                        {/*            <div className={`flex items-center`}>50 <span className="ml-1 text-xl">°F</span></div>*/}
                        {/*            <div className={`ml-auto flex items-center text-xl`}>50 <span className="ml-1 text-sm">%</span></div>*/}
                        {/*        </div>*/}
                        {/*        <div className="ml-2 flex items-center text-sm">city, state</div>*/}
                        {/*    </div>*/}

                        {/*    <div className="col-span-1 flex flex-col items-center justify-center">*/}
                        {/*        <SunIcon className="size-10 text-yellow-400"/>*/}
                        {/*    </div>*/}

                        {/*</div>*/}


                    </div>

                    <div className={`col-span-4 row-span-2 col-start-3 row-start-2 border ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white'} rounded-lg`}>3</div>
                    <div className={`col-span-2 col-start-3 row-start-4 border ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white'} rounded-lg`}>4</div>

                    {/*Course Data*/}
                    <div className={`col-span-2 col-start-5 row-start-4 border ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white'} rounded-lg`}>
                        <div className="mx-auto max-w-7xl">
                            <div className="grid gap-px grid-cols-2">
                                {CourseStats.map((stat) => (
                                    <div key={stat.name} className="px-4 py-2">
                                        <Link to={"/CourseInfo"}>
                                            <p className="text-sm font-medium text-green-600">{stat.name}</p>
                                            <p className="mt-0.5 flex items-baseline gap-x-2">
                                                <span className="text-2xl font-semibold tracking-tight text-white">{stat.value}</span>
                                                {stat.unit ? <span className="text-sm text-gray-400">{stat.unit}</span> : null}
                                            </p>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>


            </div>
        </div>
    );
};

export default Home;


//AIzaSyDN45vy87lqI2YHWycqn-BKnsC1jMHPd2E
